import { useEffect, useState } from 'react';
import { C } from '@ridgewill/brand';
import {
  audienceKind,
  audienceLabel,
  type AudienceKind,
  type HasAudience,
} from '../lib/agreement-audience.js';
import {
  ApiError,
  adminApi,
  type AgreementDetail as Detail,
  type AgreementDraft,
  type PublishResult,
} from '../lib/api.js';
import {
  EMPTY_FORM,
  SUMMARY_LIMIT,
  draftBody,
  draftLabel,
  formFromDraft,
  isDirty,
  missingForPublish,
  summaryProblem,
  toInstant,
  type VersionForm,
} from '../lib/agreement-drafts.js';
import { openPreview } from '../lib/legal-preview.js';
import {
  PUBLIC_SECTIONS,
  SITE_URL,
  draftFrom,
  publicPatch,
  publicUrl,
  refusedPublicField,
  sectionLabel,
  slugProblem,
  type PublicDraft,
  type PublicSection,
} from '../lib/agreement-public.js';
import { describe, useAsync, useAction } from '../lib/useAsync.js';
import { Select } from '../components/Select.js';
import { count } from '../lib/format.js';
import { Modal } from '../components/Modal.js';
import { RecordScreen } from '../components/Record.js';
import { useCan } from '../lib/capabilities.js';
import { inputStyle } from '../components/Screen.js';
import {
  AsyncBoundary,
  Badge,
  Button,
  EmptyState,
  Field,
  HoldButton,
  Identity,
  Note,
  Num,
  OutcomeNote,
  Panel,
  PanelBody,
  Table,
  Td,
  When,
} from '../components/ui.js';

/**
 * One agreement: its versions, who accepted, and the button that publishes.
 *
 * ── The most consequential control in this portal ────────────────────
 *
 * Publishing writes an immutable version and fans the slug into the cached
 * `pendingAgreements` of every affected user in one statement. There is no undo:
 * versions are immutable, and acceptances are legal evidence with
 * `onDelete: Restrict` behind them, so the database itself refuses to lose them.
 *
 * That is why publishing is a MODAL inside a modal rather than a button on the
 * screen. The old portal had it behind an inline disclosure at the bottom of the
 * page, which was the same instinct — keep it away from a naked click — reached
 * by the wrong means, since it also put it a thousand rows down.
 *
 * The response is reported in full. `fannedOut` says how many people now owe
 * this, and `deferred` says whether anything happened at all: a future-dated
 * version waits for the activation sweep, so a publish that looks like it did
 * nothing is the expected outcome rather than a failure.
 */
export function AgreementDetail({ id }: { id: string }) {
  const { state, reload } = useAsync(() => adminApi.agreement(id), [id]);
  /** The editor: closed, a new version, or a draft by id. */
  const [editing, setEditing] = useState<null | { draftId: string | null }>(null);
  /** The last publish's outcome, said on the page because the editor closes. */
  const [published, setPublished] = useState<string | null>(null);
  /**
   * Retire, Restore and Publish a version are HIDDEN without `agreement:write`
   * (Jon, 2026-09-16, org-admin#134). They had no gate at all before this, so
   * a Support or Auth Admin got live buttons the server then refused.
   */
  const mayWrite = useCan('agreement:write');
  const onPublished = (message: string) => {
    setEditing(null);
    setPublished(message);
    reload(true);
  };

  return (
    <AsyncBoundary state={state}>
      {(a) => (
        <RecordScreen
          back={{ to: '/legal', label: 'Legal' }}
          title={a.name}
          identifier={a.slug}
          /* Said rather than offered: auth's PATCH takes `active` and nothing
             else, so there is no edit to put behind a button. A different
             audience is a different agreement. */
          description={
            a.kind === 'notice'
              ? 'A notice: published on ridgewill.com and never owed or accepted. Fixed when it was created; something people must accept needs a new agreement.'
              : `${OWED_BY[audienceKind(a)](a)} Fixed when it was created; a different audience needs a new agreement.`
          }
          badges={
            <>
              {/* Never blank: that would read as missing data on the field
                  that decides who owes it. */}
              {a.kind === 'notice' ? <Badge>notice</Badge> : <Badge>{audienceLabel(a)}</Badge>}
              {a.active ? null : <Badge tone="danger">retired</Badge>}
            </>
          }
          actions={
            mayWrite ? (
            <span style={{ display: 'flex', gap: '.5rem', alignItems: 'center' }}>
              <RetireToggle
                agreement={a}
                onChanged={() => {
                  reload();
                }}
              />
              <Button tone="danger" onClick={() => setEditing({ draftId: null })} disabled={!a.active}>
                Publish a version
              </Button>
            </span>
            ) : undefined
          }
        >
          {/*
            STACKED PANELS, NOT TABS — Jon, 2026-08-28, and the distinction is
            the point of having both shapes.

            Users and Applications got tabs because their sections answer
            genuinely separate questions: somebody's passkeys have nothing to do
            with their sessions, and reading one while the other is on screen
            costs attention rather than buying anything.

            An agreement is one thing. The versions and who accepted them are two
            views of the same fact, and the question people actually arrive with —
            "is the current version being accepted" — needs both at once. Tabs
            would make that question two clicks and a memory test.
          */}
          {published ? (
            <OutcomeNote outcome={{ ok: true, message: published }} />
          ) : null}

          {mayWrite && a.drafts.length > 0 ? (
            <Drafts
              agreement={a}
              onOpen={(draftId) => setEditing({ draftId })}
              onDeleted={() => reload(true)}
            />
          ) : null}

          <Panel title={`Versions (${count(a.versions.length)})`}>
            {a.versions.length === 0 ? (
              <EmptyState
                title="No versions published"
                suggestion={
                  a.kind === 'notice'
                    ? 'Nothing to show on the website yet. Publishing the first version is what puts it there.'
                    : 'Nobody owes this yet. Publishing the first version is what puts it in front of people.'
                }
              />
            ) : (
              <Table
                columns={[
                  'Version',
                  'Effective',
                  'Activated',
                  /* A notice is never accepted, so both columns would be a
                     permanent "no" and "0" that read like a problem. */
                  ...(a.kind === 'notice' ? ['Acceptance'] : ['Re-accept', 'Accepted']),
                  'Hash',
                  ...(a.publicSlug ? [''] : []),
                ]}
              >
                {a.versions.map((v) => (
                  <tr key={v.id}>
                    <Td>
                      <strong>{v.version}</strong>
                      {v.summary ? <div style={clamped}>{v.summary}</div> : null}
                    </Td>
                    <Td>
                      <When iso={v.effectiveAt} />
                    </Td>
                    <Td>
                      {/* Effective-but-not-activated is the state where a version
                          looks live and owes nobody anything — the activation
                          sweep has not run. Worth its own badge. */}
                      {v.activatedAt ? (
                        <When iso={v.activatedAt} />
                      ) : (
                        <Badge tone="warn">awaiting activation</Badge>
                      )}
                    </Td>
                    {a.kind === 'notice' ? (
                      <Td>
                        <span style={{ ...sub, whiteSpace: 'nowrap' }}>Notice: published, never accepted</span>
                      </Td>
                    ) : (
                      <>
                        <Td>
                          {v.requiresReacceptance ? <Badge tone="warn">yes</Badge> : <Badge>no</Badge>}
                        </Td>
                        <Td>
                          <Num value={v.acceptanceCount} />
                        </Td>
                      </>
                    )}
                    <Td mono>{v.contentHash.slice(0, 12)}</Td>
                    {a.publicSlug ? (
                      <Td>
                        {/* A path, not a link, in a build that knows no site
                            (staging has none): a link to this portal's own
                            origin would open the portal. */}
                        {SITE_URL ? (
                          <a
                            href={publicUrl(SITE_URL, a.publicSlug, v.version)}
                            target="_blank"
                            rel="noreferrer"
                            style={link}
                          >
                            View on ridgewill.com
                          </a>
                        ) : (
                          <span style={sub}>{publicUrl('', a.publicSlug, v.version)}</span>
                        )}
                      </Td>
                    ) : null}
                  </tr>
                ))}
              </Table>
            )}
          </Panel>

          <Panel title="On ridgewill.com">
            <OnTheWebsite agreement={a} mayWrite={mayWrite} onSaved={reload} />
          </Panel>

          {a.kind === 'notice' ? null : (
            <Panel title="Acceptance log">
              <Acceptances id={a.id} />
            </Panel>
          )}

          {editing ? (
            editing.draftId ? (
              <OpenDraft
                agreement={a}
                draftId={editing.draftId}
                onClose={() => setEditing(null)}
                onDraftsChanged={() => reload(true)}
                onPublished={onPublished}
              />
            ) : (
              <VersionEditor
                agreement={a}
                draft={null}
                onClose={() => setEditing(null)}
                onDraftsChanged={() => reload(true)}
                onPublished={onPublished}
              />
            )
          ) : null}
        </RecordScreen>
      )}
    </AsyncBoundary>
  );
}

function RetireToggle({
  agreement,
  onChanged,
}: {
  agreement: { id: string; active: boolean };
  onChanged: () => void;
}) {
  const action = useAction();
  const toggle = () =>
    void action
      .run(async () => {
        const r = await adminApi.setAgreementActive(agreement.id, !agreement.active);
        // `cachesChanged` is the reach of the action. Retiring drops the
        // slug from every pending cache, and the number is how you know
        // whether that touched three people or thirty thousand.
        return agreement.active
          ? `Retired. Dropped from ${count(r.cachesChanged)} pending caches.`
          : `Restored. Added to ${count(r.cachesChanged)} pending caches.`;
      })
      .then(onChanged);

  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: '.6rem' }}>
      {action.outcome ? (
        <span
          style={{
            fontSize: '.79rem',
            color: action.outcome.ok ? C.successText : C.dangerText,
            maxWidth: 320,
          }}
        >
          {action.outcome.message}
        </span>
      ) : null}
      {/* Retiring releases everybody from the agreement at once, so it holds.
          Restoring re-adds an obligation it already had, so it stays a click. */}
      {agreement.active ? (
        <HoldButton
          disabled={action.running}
          title="Hold for a second and a half to retire this document. Nobody will owe it until it is restored."
          onHold={toggle}
        >
          Retire
        </HoldButton>
      ) : (
        <Button disabled={action.running} onClick={toggle}>
          Restore
        </Button>
      )}
    </span>
  );
}

/**
 * Writes a version: as a draft to come back to, or published. Opened empty
 * from "Publish a version", or from a draft's Open with that draft loaded.
 *
 * Save draft keeps the editor open and repeats as often as wanted; the first
 * save creates the draft and later ones update it. Publishing a draft saves
 * any unsaved typing into it first, then publishes it through the draft's
 * own endpoint, which deletes it, so a published draft cannot linger beside
 * its version.
 */
function VersionEditor({
  agreement,
  draft,
  onClose,
  onDraftsChanged,
  onPublished,
}: {
  agreement: EditorAgreement;
  /** The draft being reopened, already loaded, or null for a new version. */
  draft: AgreementDraft | null;
  onClose: () => void;
  onDraftsChanged: () => void;
  onPublished: (message: string) => void;
}) {
  const initial = draft ? formFromDraft(draft) : EMPTY_FORM;
  const [form, setForm] = useState<VersionForm>(initial);
  const [saved, setSaved] = useState<VersionForm>(initial);
  const [draftId, setDraftId] = useState<string | null>(draft?.id ?? null);
  const [confirmingClose, setConfirmingClose] = useState(false);
  const action = useAction();
  const notice = agreement.kind === 'notice';
  const dirty = isDirty(form, saved, notice);
  const missing = missingForPublish(form);
  const summaryError = summaryProblem(form.summary);
  const set = (over: Partial<VersionForm>) => {
    setForm((f) => ({ ...f, ...over }));
    setConfirmingClose(false);
  };

  // Closing the tab or reloading with unsaved typing gets the browser's own
  // "leave site?" prompt. The modal's three ways out go through `close`.
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const close = () => (dirty && !action.running ? setConfirmingClose(true) : onClose());

  const saveDraft = async (): Promise<void> => {
    const sent = form;
    const body = draftBody(sent, notice);
    const d = draftId
      ? await adminApi.updateDraft(agreement.id, draftId, body)
      : await adminApi.createDraft(agreement.id, body);
    setDraftId(d.id);
    setSaved(sent);
    onDraftsChanged();
  };

  const publish = () => {
    let message = '';
    void action
      .run(async () => {
        const effectiveAt = toInstant(form.effectiveAt) ?? '';
        let r: PublishResult;
        if (draftId) {
          if (dirty) await saveDraft();
          r = await adminApi.publishDraft(agreement.id, draftId, { effectiveAt });
        } else {
          r = await adminApi.publishVersion(agreement.id, {
            version: form.version.trim(),
            content: form.content,
            contentFormat: 'markdown',
            summary: form.summary.trim() || undefined,
            effectiveAt,
            // Ignored by auth for a notice; not sent, so the request says
            // what the form showed.
            requiresReacceptance: notice ? undefined : form.requiresReacceptance,
          });
        }
        message = outcomeOf(r, notice);
        return message;
      })
      .then((ok) => ok && onPublished(message));
  };

  return (
    <Modal
      title={draftId ? `Draft: ${draftLabel(form)}` : 'Publish a version'}
      subtitle={draftId ? (dirty ? 'Unsaved changes' : 'Saved as a draft. Nothing is published.') : undefined}
      width={820}
      onClose={close}
      footer={
        confirmingClose ? (
          <div style={footRow}>
            <span style={{ fontSize: '.85rem', color: C.warnText, marginRight: 'auto' }}>
              You have unsaved changes. Close without saving them?
            </span>
            <Button onClick={() => setConfirmingClose(false)}>Keep editing</Button>
            <Button tone="danger" onClick={onClose}>
              Discard changes
            </Button>
          </div>
        ) : (
          <div style={footRow}>
            <Button
              onClick={() => openPreview(agreement, form)}
              title="Opens in a new tab, as ridgewill.com/legal will show it."
            >
              Preview
            </Button>
            <span style={{ marginRight: 'auto' }} />
            <Button onClick={close}>Cancel</Button>
            <Button
              disabled={action.running || !!summaryError || (!!draftId && !dirty)}
              title={
                draftId && !dirty
                  ? 'Nothing changed since the last save.'
                  : 'Keep this to finish later. Nothing is published.'
              }
              onClick={() =>
                void action.run(async () => {
                  await saveDraft();
                  return 'Saved as a draft. Nothing is published; open it from Drafts to carry on.';
                })
              }
            >
              {action.running ? 'Saving…' : 'Save draft'}
            </Button>
            <HoldButton
              title={
                missing.length
                  ? `Still needed before publishing: ${missing.join(', ')}.`
                  : 'Hold for a second and a half to publish. A published version cannot be changed or withdrawn.'
              }
              disabled={action.running || missing.length > 0 || !!summaryError}
              onHold={publish}
            >
              {action.running ? 'Working…' : 'Publish'}
            </HoldButton>
          </div>
        )
      }
    >
      {notice ? (
        <Note tone="danger">
          Publishing is immutable and not reversible. It writes the version and, with a web address,
          puts it on ridgewill.com. Nobody is asked to accept a notice. Save draft keeps it private
          until it is ready.
        </Note>
      ) : (
        <Note tone="danger">
          Publishing is immutable and not reversible. It writes the version and adds it to the
          pending list of everyone in scope. Retiring the agreement afterwards removes the
          requirement, but the version and every acceptance stay on the record permanently. Save
          draft keeps it private until it is ready.
        </Note>
      )}

      <OutcomeNote outcome={action.outcome} />

      <Field label="Version" hint="Free text, unique within this agreement: “1.0”, or a date.">
        <input value={form.version} onChange={(e) => set({ version: e.target.value })} style={inputStyle} />
      </Field>
      <Field label="Content" hint="Markdown. Stored inline and hashed; it cannot be edited once published.">
        <textarea
          value={form.content}
          onChange={(e) => set({ content: e.target.value })}
          rows={14}
          style={editorStyle}
        />
      </Field>
      <Field
        label="Summary"
        error={summaryError ?? undefined}
        hint={`${
          notice
            ? 'What changed. Nobody is asked to accept a notice.'
            : 'What changed. This is what people see when asked to accept again.'
        } Markdown is supported. ${form.summary.length.toLocaleString('en-US')} of ${SUMMARY_LIMIT.toLocaleString('en-US')} characters.`}
      >
        <textarea
          value={form.summary}
          onChange={(e) => set({ summary: e.target.value })}
          rows={5}
          style={editorStyle}
        />
      </Field>
      <Field
        label="Effective from"
        hint="No default, deliberately. A future date defers the fan-out to the activation sweep, so nothing happens when you click publish, and that is a surprise worth choosing rather than inheriting."
      >
        <input
          type="datetime-local"
          value={form.effectiveAt}
          onChange={(e) => set({ effectiveAt: e.target.value })}
          style={inputStyle}
        />
      </Field>
      {notice ? null : (
        <label style={{ display: 'flex', alignItems: 'center', gap: '.4rem', fontSize: '.86rem' }}>
          <input
            type="checkbox"
            checked={form.requiresReacceptance}
            onChange={(e) => set({ requiresReacceptance: e.target.checked })}
          />
          Material change: everyone must accept again
        </label>
      )}
      <div style={{ fontSize: '.85rem', display: 'grid', gap: '.3rem' }}>
        <span style={{ fontWeight: 600, color: C.textMid }}>Web address</span>
        {agreement.publicSlug ? (
          <>
            {/* What auth writes into documentUrl when the publish omits one. */}
            <code style={mono}>
              {form.version.trim()
                ? publicUrl(SITE_URL, agreement.publicSlug, form.version.trim())
                : `${publicUrl(SITE_URL, agreement.publicSlug)}<version>/`}
            </code>
            <span style={sub}>
              Shown from its effective date; before that, ridgewill.com lists it as upcoming.
            </span>
          </>
        ) : (
          <span style={sub}>
            None. This is not on ridgewill.com; turn on Show on ridgewill.com first if it should be.
          </span>
        )}
      </div>
    </Modal>
  );
}

/** Loads a draft's content, which the list leaves out, then opens it. */
function OpenDraft({
  agreement,
  draftId,
  onClose,
  onDraftsChanged,
  onPublished,
}: {
  agreement: EditorAgreement;
  draftId: string;
  onClose: () => void;
  onDraftsChanged: () => void;
  onPublished: (message: string) => void;
}) {
  const { state } = useAsync(() => adminApi.agreementDraft(agreement.id, draftId), [agreement.id, draftId]);
  if (state.status === 'ready') {
    return (
      <VersionEditor
        agreement={agreement}
        draft={state.data}
        onClose={onClose}
        onDraftsChanged={onDraftsChanged}
        onPublished={onPublished}
      />
    );
  }
  return (
    <Modal title="Open a draft" onClose={onClose}>
      {state.status === 'failed' ? <Note tone="danger">{state.message}</Note> : <Note>Opening…</Note>}
    </Modal>
  );
}

/**
 * Drafts, above the versions they will become. Shown only to someone who can
 * write, since only they can open or publish one, and only when there are
 * any: an empty Drafts panel on every agreement would be noise.
 */
function Drafts({
  agreement,
  onOpen,
  onDeleted,
}: {
  agreement: Pick<Detail, 'id' | 'drafts'>;
  onOpen: (draftId: string) => void;
  onDeleted: () => void;
}) {
  const action = useAction();
  return (
    <Panel title={`Drafts (${count(agreement.drafts.length)})`}>
      {action.outcome && !action.outcome.ok ? (
        <PanelBody>
          <OutcomeNote outcome={action.outcome} />
        </PanelBody>
      ) : null}
      <Table columns={['Version', 'Last edited', 'By', '']}>
        {agreement.drafts.map((d) => (
          <tr key={d.id}>
            <Td>
              {d.version?.trim() ? <strong>{d.version}</strong> : <span style={{ color: C.muted }}>untitled</span>}
              {d.summary ? <div style={clamped}>{d.summary}</div> : null}
            </Td>
            <Td>
              <When iso={d.updatedAt} />
            </Td>
            <Td>{d.updatedByName ?? '—'}</Td>
            <Td>
              <span style={{ display: 'flex', gap: '.5rem', justifyContent: 'flex-end' }}>
                <Button onClick={() => onOpen(d.id)}>Open</Button>
                <HoldButton
                  disabled={action.running}
                  title="Hold for a second and a half to delete this draft. Nothing published is affected."
                  onHold={() =>
                    void action
                      .run(async () => {
                        await adminApi.deleteDraft(agreement.id, d.id);
                        return 'Deleted.';
                      })
                      .then((ok) => ok && onDeleted())
                  }
                >
                  Delete
                </HoldButton>
              </span>
            </Td>
          </tr>
        ))}
      </Table>
    </Panel>
  );
}

/**
 * The "On ridgewill.com" panel: whether this appears on ridgewill.com/legal,
 * at what address, under which heading, and how it is described there.
 *
 * ── The website reads this, it does not copy it ──────────────────────
 *
 * ridgewill.com/legal renders from a public auth endpoint at the edge, so a
 * save here is the website changing (Jon, 2026-10-15). The address in
 * particular is what people bookmark and what auth writes into every new
 * version's documentUrl, which is why the preview is live: the URL you are
 * about to give out is on screen before you save it.
 *
 * Editing needs `agreement:write`, like Retire. Without it the panel still
 * says what the website shows, because "is this on the site" is a question
 * Support answers too.
 */
function OnTheWebsite({
  agreement,
  mayWrite,
  onSaved,
}: {
  agreement: Detail;
  mayWrite: boolean;
  onSaved: () => void;
}) {
  const [draft, setDraft] = useState<PublicDraft>(() => draftFrom(agreement));
  const [slugRefused, setSlugRefused] = useState<string | null>(null);
  const action = useAction();
  const patch = publicPatch(agreement, draft);
  const set = (over: Partial<PublicDraft>) => {
    setDraft((d) => ({ ...d, ...over }));
    action.clear();
  };

  if (!mayWrite) {
    return (
      <PanelBody>
        {agreement.publicSlug ? (
          <dl style={facts}>
            <dt>Web address</dt>
            <dd>
              {SITE_URL ? (
                <a href={publicUrl(SITE_URL, agreement.publicSlug)} target="_blank" rel="noreferrer" style={link}>
                  {publicUrl(SITE_URL, agreement.publicSlug)}
                </a>
              ) : (
                <code style={mono}>{publicUrl('', agreement.publicSlug)}</code>
              )}
            </dd>
            <dt>Section</dt>
            <dd>{sectionLabel(agreement.publicSection)}</dd>
            <dt>Order</dt>
            <dd>{agreement.publicOrder}</dd>
            <dt>Description</dt>
            <dd>{agreement.publicDescription || '—'}</dd>
            <dt>Version history</dt>
            <dd>{agreement.publicVersioned ? 'Shown' : 'Hidden'}</dd>
          </dl>
        ) : (
          <Note>Not on ridgewill.com.</Note>
        )}
      </PanelBody>
    );
  }

  const slug = draft.slug.trim();
  const slugError = slugRefused ?? (draft.shown ? slugProblem(slug) : null);

  return (
    <PanelBody>
      <div style={{ display: 'grid', gap: '.8rem', maxWidth: 560 }}>
        <OutcomeNote outcome={slugRefused ? null : action.outcome} />
        <label style={check}>
          <input
            type="checkbox"
            checked={draft.shown}
            onChange={(e) => {
              set({ shown: e.target.checked });
              setSlugRefused(null);
            }}
          />
          Show on ridgewill.com
        </label>

        {draft.shown ? (
          <>
            <Field
              label="Web address"
              hint="Lowercase letters, digits and hyphens. Changing it later breaks every link to the old one."
              error={slugError ?? undefined}
            >
              <input
                value={draft.slug}
                onChange={(e) => {
                  set({ slug: e.target.value });
                  setSlugRefused(null);
                }}
                style={inputStyle}
                aria-label="Web address"
              />
            </Field>
            {/* Live, and outside the Field so an error under it does not
                replace it: this is the address about to be given out. */}
            <code style={{ ...mono, color: slugError ? C.muted : C.text }}>
              {publicUrl(SITE_URL, slug || '…')}
            </code>
            <div style={{ display: 'grid', gap: '.3rem', fontSize: '.85rem' }}>
              <span style={{ fontWeight: 600, color: C.textMid }}>Section</span>
              <Select<PublicSection | ''>
                block
                label="Section"
                value={draft.section}
                onChange={(v) => set({ section: v })}
                options={[{ value: '', label: 'No section' }, ...PUBLIC_SECTIONS]}
              />
            </div>
            <Field label="Order" hint="Position within the section; lower comes first.">
              <input
                type="number"
                step={1}
                value={draft.order}
                onChange={(e) => set({ order: e.target.value })}
                style={{ ...inputStyle, maxWidth: 120 }}
              />
            </Field>
            <Field label="One-line description" hint="Shown under the title on ridgewill.com/legal.">
              <input
                value={draft.description}
                onChange={(e) => set({ description: e.target.value })}
                style={inputStyle}
              />
            </Field>
            <label style={check}>
              <input
                type="checkbox"
                checked={draft.versioned}
                onChange={(e) => set({ versioned: e.target.checked })}
              />
              Show version history
            </label>
          </>
        ) : (
          <span style={sub}>
            Not on the website. Turning this off and saving takes the page down; the other settings
            are kept for when it comes back.
          </span>
        )}

        <span style={{ display: 'flex', gap: '.5rem', alignItems: 'center' }}>
          <Button
            disabled={action.running || patch === null || typeof patch === 'string'}
            title={typeof patch === 'string' ? patch : undefined}
            onClick={() => {
              if (patch === null || typeof patch === 'string') return;
              void action
                .run(async () => {
                  setSlugRefused(null);
                  try {
                    await adminApi.setAgreementPublic(agreement.id, patch);
                  } catch (e) {
                    if (e instanceof ApiError && refusedPublicField(e.code) === 'slug') {
                      setSlugRefused(describe(e));
                    }
                    throw e;
                  }
                  return patch.publicSlug === null ? 'Saved. It is no longer on ridgewill.com.' : 'Saved.';
                })
                .then((ok) => ok && onSaved());
            }}
          >
            {action.running ? 'Saving…' : 'Save'}
          </Button>
          {patch === null ? null : (
            <Button onClick={() => setDraft(draftFrom(agreement))} disabled={action.running}>
              Discard changes
            </Button>
          )}
        </span>
      </div>
    </PanelBody>
  );
}

function Acceptances({ id }: { id: string }) {
  const { state } = useAsync(() => adminApi.acceptances(id), [id]);

  return (
    <AsyncBoundary state={state}>
      {(d) =>
        d.acceptances.length === 0 ? (
          <EmptyState
            title="Nobody has accepted a version of this yet"
            suggestion="Acceptances appear here as people agree, and stay on the record permanently."
          />
        ) : (
          <>
            <Table columns={['Who', 'Version', 'Accepted', 'Withdrawn', 'IP']}>
              {d.acceptances.map((r) => (
                <tr key={r.id}>
                  <Td>
                    <Identity
                      email={r.user.email}
                      name={r.user.name}
                      pseudonymized={r.user.pseudonymized}
                    />
                  </Td>
                  <Td>{r.version}</Td>
                  <Td>
                    <When iso={r.acceptedAt} />
                  </Td>
                  <Td>
                    {/* Withdrawals are shown rather than filtered out. This table
                        is the record, and a record that quietly omits
                        withdrawals is not one. */}
                    {r.withdrawnAt ? (
                      <Badge tone="danger">
                        <When iso={r.withdrawnAt} />
                      </Badge>
                    ) : (
                      '—'
                    )}
                  </Td>
                  <Td mono>{r.ipAddress ?? '—'}</Td>
                </tr>
              ))}
            </Table>
            <PanelBody>
              <Note>
                Showing {count(d.acceptances.length)} of {count(d.total)}. Capped by the server,
                because this is an unbounded read over legal-evidence rows.
              </Note>
            </PanelBody>
          </>
        )
      }
    </AsyncBoundary>
  );
}

/** What a publish did, in full: see the header of AgreementDetail. */
function outcomeOf(r: PublishResult, notice: boolean): string {
  if (notice) {
    return r.deferred
      ? 'Published with a future effective date. ridgewill.com shows it as upcoming until then.'
      : 'Published. Nobody is asked to accept a notice.';
  }
  return r.deferred
    ? 'Published with a future effective date. Nobody owes it yet. The activation job fans it out when it comes into force.'
    : `Published. ${count(r.fannedOut)} people now owe this version.`;
}

type EditorAgreement = Pick<Detail, 'id' | 'name' | 'kind' | 'publicSlug' | 'publicVersioned'>;

const OWED_BY: Record<AudienceKind, (a: HasAudience) => string> = {
  org: () => 'Owed by every account.',
  app: (a) => `Owed by people holding a role that grants ${a.appSlug}.`,
  group: (a) => `Owed only by members of the ${a.groupSlug} security group, whichever app asks.`,
};

const sub = { color: C.muted, fontSize: '.78rem', marginTop: 2 };
const check = { display: 'flex', alignItems: 'center', gap: '.4rem', fontSize: '.86rem' };
const facts: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'max-content 1fr',
  gap: '.35rem 1rem',
  margin: 0,
  fontSize: '.85rem',
};
const link = { color: C.brand, fontWeight: 600, textDecoration: 'none', whiteSpace: 'nowrap' as const };
/** Content and Summary: monospace, and as tall as the person dragging the
 *  corner wants. */
const editorStyle: React.CSSProperties = {
  ...inputStyle,
  resize: 'vertical',
  minHeight: '6rem',
  fontFamily: 'ui-monospace, monospace',
  lineHeight: 1.5,
};
const footRow: React.CSSProperties = { display: 'flex', gap: '.6rem', alignItems: 'center', flexWrap: 'wrap', width: '100%' };
/** A markdown summary can be pages long; a table row shows its start. */
const clamped: React.CSSProperties = {
  ...sub,
  whiteSpace: 'pre-wrap',
  display: '-webkit-box',
  WebkitLineClamp: 3,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  maxWidth: 520,
};
const mono = { fontFamily: 'ui-monospace, monospace', fontSize: '.8rem', wordBreak: 'break-all' as const };
